import {
    ComponentSettingsChartMeasureField,
    ContentSettings,
    DatasetField,
    fieldToChartMeasuredField,
    generateMeasureFieldId,
    getFirstAllowedFieldAsMeasure,
    getMeasureFieldOptionForFormUId,
    fieldToKpiChartMeasuredField,
} from '@report-content';
import { useState } from 'react';
import { arrayMoveImmutable } from 'array-move';
import usePalette from 'components/report-content/hooks/usePalette';
import useChartColorPalette from 'components/report-content/hooks/useChartColorPalette';
import { FormRendererAPIType } from 'components/common/form';
export function useMeasureField<T extends ComponentSettingsChartMeasureField>(
    datasetFields: DatasetField[],
    settingsMeasureFields: Array<T>,
    contentSettings: ContentSettings,
    kind: 'chart' | 'kpi' = 'chart',
) {
    const chartColorPalette = useChartColorPalette();
    const { getPaletteColor } = usePalette(contentSettings.context, contentSettings.elementId, chartColorPalette);

    const getInitialMeasureFields = (): Array<T> => {
        // If MeasureFields are empty - add first fields as measure;
        if (settingsMeasureFields.length == 0) {
            const measureField = getFirstAllowedFieldAsMeasure<T>(
                datasetFields,
                [],
                (kind == 'kpi' ? fieldToKpiChartMeasuredField : fieldToChartMeasuredField) as (
                    datasetField: DatasetField,
                ) => T,
            );
            if (!measureField) {
                return [];
            }
            if (kind == 'kpi') {
                //@ts-ignore
                measureField.is_primary = 'Y';
            }
            measureField.color = getPaletteColor(measureField.reference_name, 0, false) ?? '';
            return [measureField];
        } else {
            // Legacy measure fields has no unique id field
            return settingsMeasureFields.map((f) => ({ ...f, uId: f.uId ?? generateMeasureFieldId() }));
        }
    };

    const [measureFields, setMeasureFields] = useState<Array<T>>(getInitialMeasureFields());
    const [selectedMeasure, setSelectedMeasure] = useState<T | null>();

    const handleSelectMeasureField = (field: T) => {
        setSelectedMeasure(field);
    };
    const handleMeasureFieldsSort = (oldIndex: number, newIndex: number) => {
        setMeasureFields(arrayMoveImmutable(measureFields, oldIndex, newIndex));
    };
    const handleMeasureFieldUpdate = (field: T) => {
        const index = measureFields.findIndex((f) => f.uId == field.uId);
        if (index == -1) {
            return;
        }
        const list = measureFields.slice();
        list[index] = field;
        setMeasureFields(list);
        setSelectedMeasure(null);
    };

    const handleAddMeasureField = (form: FormRendererAPIType) => {
        const measureField = getFirstAllowedFieldAsMeasure<T>(
            datasetFields,
            measureFields,
            (kind == 'kpi' ? fieldToKpiChartMeasuredField : fieldToChartMeasuredField) as (
                datasetField: DatasetField,
            ) => T,
        );
        if (!measureField) {
            return;
        }
        for (let i = 0; i < 100; i++) {
            let color = getPaletteColor(measureField.reference_name, i, false) ?? '';
            if (measureFields.findIndex((f) => f.color.toLowerCase() == color.toLowerCase()) == -1) {
                measureField.color = color;
                break;
            }
        }
        // Set default display type
        const fieldFormDisplayType = getMeasureFieldOptionForFormUId(measureField, 'display_type');
        form.hookFormSetValue(fieldFormDisplayType, measureField.display_type);

        const list = measureFields.slice();
        list.push(measureField);
        setMeasureFields(list);
    };

    const handleRemoveMeasureField = (field: T) => {
        const index = measureFields.findIndex((f) => f.uId == field.uId);
        if (index == -1) {
            return false;
        }
        const list = measureFields.slice();
        list.splice(index, 1);
        setMeasureFields(list);
    };

    return {
        measureFields,
        selectedMeasure,
        setSelectedMeasure,
        handleSelectMeasureField,
        handleMeasureFieldsSort,
        handleMeasureFieldUpdate,
        handleAddMeasureField,
        handleRemoveMeasureField,
    };
}
